import React, { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import "./PdfMapaFinanceiro.scss";

const printStyles = `
    @media print {
      @page {
        size: portrait;
        margin: 15mm;
      }
      
      table {
        page-break-inside: auto;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      
      .subtotal-row {
        page-break-inside: avoid;
      }
      
      .pdf-mapaFinanceiro-cabecalho {
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      
      .pdf-mapaFinanceiro__titulo {
        page-break-after: avoid;
      }
      
      .headingLine {
        page-break-after: avoid;
      }
    }
  `;

function PdfMapaFinanceiro({ mapaData, showSingleP }) {
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Mapa de receituário`,
    onAfterPrint: () => {
      showSingleP();
    },
  });

  const normalizarNomes = (nome) => {
    if (!nome) return "";
    // normalizar os nomes
    const words = nome.toLowerCase().split(" ");
    // Uppercase the first letter of each word
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1),
    );
    return capitalizedWords.join(" ");
  };

  // Preparar dados agrupados para renderização
  const todosDocumentos = [];
  mapaData.dados.forEach((grupo, indexGrupo) => {
    // Adicionar marcador de linha vazia antes (exceto no primeiro grupo)
    if (indexGrupo > 0) {
      todosDocumentos.push({ tipo: "linha_vazia" });
    }

    // Adicionar documentos do grupo
    grupo.documentos.forEach((doc) => {
      todosDocumentos.push({ ...doc, tipo: "documento" });
    });

    // Adicionar linha de subtotal (SEM linha vazia depois)
    todosDocumentos.push({
      tipo: "subtotal",
      tipoDocumento: grupo.tipo,
      subtotal: grupo.subtotal,
    });
  });

  // Calcular total geral
  const total = mapaData.dados.reduce(
    (accumulator, grupo) => accumulator + grupo.subtotal,
    0,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    handlePrint();
  }, [handlePrint]);

  return (
    <>
      <style>{printStyles}</style>
      <div
        ref={componentRef}
        className={`pdf-mapaFinanceiro-wrapper ${mapaData.clinica?.configuracoes?.configuracoes?.impressaoPretoEBranco?.ativo ? " pdf-wrapper--pretoebranco" : ""}`}
      >
        {/* Cabeçalho - apenas uma vez */}
        <div className="pdf-mapaFinanceiro-cabecalho">
          <img
            src={`${process.env.REACT_APP_BACKEND_LINK}/${mapaData.clinicaData.logotipo}`}
            alt=""
          />
          <div className="pdf-mapaFinanceiro-cabecalho--infos">
            <span>{normalizarNomes(mapaData.clinicaData.nome)}</span>
            <span>
              Telef: {mapaData.clinicaData.contacto}{" "}
              {mapaData.clinicaData.contacto2
                ? "/" + mapaData.clinicaData.contacto2
                : ""}
              {mapaData.clinicaData.contacto3
                ? "/" + mapaData.clinicaData.contacto3
                : ""}
            </span>
            <span>{mapaData.clinicaData.endereco}</span>
            <span>NIF: {mapaData.clinicaData.nif}</span>
          </div>
        </div>

        {/* Título */}
        <span className="pdf-mapaFinanceiro__titulo">
          Mapa Movimentos Mensal, de {mapaData.dataInicial} a{" "}
          {mapaData.dataFinal}
        </span>

        {/* Tabela completa */}
        <div className="pdf-mapaFinanceiro__tabela">
          <table>
            <tbody>
              {/* Cabeçalho da tabela */}
              <tr className="headingLine">
                <th className="pdf-orc-left">Origem</th>
                <th className="pdf-orc-left">Nif Entidade</th>
                <th className="pdf-orc-left">Designação Entidade</th>
                <th className="pdf-orc-left">Serie</th>
                <th className="pdf-orc-left">Tipo Doc</th>
                <th className="pdf-orc-left">Nr Doc</th>
                <th className="pdf-orc-left">Data</th>
                <th>Valor</th>
              </tr>

              {/* Todos os documentos */}
              {todosDocumentos.map((item, i) => {
                // Linha vazia
                if (item.tipo === "linha_vazia") {
                  return (
                    <tr key={`vazia-${i}`} className="normalLine">
                      <td colSpan="8" style={{ height: "10px" }}></td>
                    </tr>
                  );
                }

                // Linha de subtotal
                if (item.tipo === "subtotal") {
                  return (
                    <tr key={`subtotal-${i}`} className="subtotal-row">
                      <td className="pdf-orc-left" colSpan="6"></td>
                      <td className="pdf-orc-left">
                        Total {item.tipoDocumento}
                      </td>
                      <td>{item.subtotal.toLocaleString("PT-BR")}</td>
                    </tr>
                  );
                }

                // Linha de documento normal
                return (
                  <tr className="normalLine" key={i}>
                    <td className="pdf-orc-left">CV</td>
                    <td className="pdf-orc-left">{item.nifEntidade}</td>
                    <td className="pdf-orc-left">
                      {normalizarNomes(item.entidade)}
                    </td>
                    <td className="pdf-orc-left">{item.serie}</td>
                    <td className="pdf-orc-left">{item.tipoDocumento}</td>
                    <td className="pdf-orc-left">{item.nrDocumento}</td>
                    <td className="pdf-orc-left">{item.data}</td>
                    <td>{item.valor.toLocaleString("PT-BR")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Resumo final */}
        <div className="pdf-mapaFinanceiro__resumo">
          <span className="pdf-mapaFinanceiro__resumo__titulo">
            Quadro Resumo Iva: {mapaData.dataInicial} a {mapaData.dataFinal}
          </span>
          <div className="pdf-mapaFinanceiro__resumo__tabela">
            <span>Valor total</span>
            <span>{total.toLocaleString("PT-BR")}</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default PdfMapaFinanceiro;
